/* eslint-disable max-lines */
import { Row, Col } from 'antd';
import { BaseSelect } from 'components/UI/BaseSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMachineList } from 'state/global/machine/operation';
import * as selector from 'state/global/machine/selector';
import { getMachineType } from 'util/machine-type';
import {
  ALL_OPTION,
  filteredFacilityMachines,
  filteredObjectMachines,
  initialState,
  parseParams
} from './utils';

export const useMachineFilter = (defaultState = initialState) => {
  const { t } = useTranslation();
  const parsedDefaultState = parseParams(defaultState);
  const dispatch = useDispatch();

  const { Option } = BaseSelect;
  const { machineListInfo: machines } = useSelector((state) => ({
    machineListInfo: selector.selectMachineListInfo(state)
  }));

  const [facilityOptions, setFacilityOptions] = useState([]);
  const [objectOptions, setObjectOptions] = useState([]);
  const [machineOptions, setMachineOptions] = useState(
    (parsedDefaultState.machineId &&
      machines?.data?.filter(
        (machine) => machine.id === parsedDefaultState.machineId
      )) ||
      []
  );
  const [selectedFacility, setSelectedFacility] = useState(
    parsedDefaultState.facility
  );
  const [selectedObject, setSelectedObject] = useState(
    parsedDefaultState.object
  );
  const [selectedMachineId, setSelectedMachineId] = useState(
    parsedDefaultState.machineId
  );

  const handleSelectedFacility = useCallback(
    (facility) => {
      let filteredMachines = filteredFacilityMachines(machines.data, facility);
      const filteredObjects =
        [
          ...new Set(
            filteredMachines
              .filter((machine) => !!machine.object)
              .map((machine) => machine.object)
          )
        ] || [];
      if (!filteredObjects.includes(selectedObject)) {
        setSelectedObject(ALL_OPTION);
        filteredMachines = filteredObjectMachines(filteredMachines, ALL_OPTION);
      } else {
        filteredMachines = filteredObjectMachines(
          filteredMachines,
          selectedObject
        );
      }

      if (!filteredMachines.includes(selectedMachineId)) {
        setSelectedMachineId(ALL_OPTION);
      }
      setObjectOptions(filteredObjects);
      setMachineOptions(filteredMachines);
      setSelectedFacility(facility);
    },
    [machines.data, selectedMachineId, selectedObject]
  );

  const handleSelectedObject = useCallback(
    (object) => {
      let filteredMachines = filteredFacilityMachines(
        machines.data,
        selectedFacility
      );
      filteredMachines = filteredObjectMachines(filteredMachines, object);
      if (!filteredMachines.includes(selectedMachineId)) {
        setSelectedMachineId(ALL_OPTION);
      }
      setMachineOptions(filteredMachines);
      setSelectedObject(object);
    },
    [machines.data, selectedFacility, selectedMachineId]
  );

  const handleSelectedMachine = useCallback((machineId) => {
    setSelectedMachineId(machineId);
  }, []);

  const selectedMachineIds = useMemo(() => {
    return selectedMachineId && selectedMachineId !== ALL_OPTION
      ? [selectedMachineId]
      : (machineOptions || []).map((machine) => machine.id);
  }, [machineOptions, selectedMachineId]);

  useEffect(() => {
    dispatch(fetchMachineList({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    setFacilityOptions([
      ...new Set(
        machines?.data
          ?.filter((machine) => !!machine.facility)
          .map((machine) => machine.facility)
      )
    ]);
    let filteredMachines = filteredFacilityMachines(
      machines.data,
      parsedDefaultState.facility
    );
    setObjectOptions([
      ...new Set(
        filteredMachines
          .filter((machine) => !!machine.object)
          .map((machine) => machine.object)
      )
    ]);
    filteredMachines = filteredObjectMachines(
      filteredMachines,
      parsedDefaultState.object
    );
    setMachineOptions(filteredMachines);
  }, [machines.data, parsedDefaultState.facility, parsedDefaultState.object]);

  const MachineFilter = useMemo(() => {
    return () => (
      <Row gutter={[12, 12]} className="machine-filter-hook">
        <Col>
          <h5 className="selection-label">{t('filterMachine.facility')}</h5>
          <BaseSelect
            className="custom-select min-w-180"
            defaultValue={selectedFacility}
            onChange={handleSelectedFacility}>
            <Option value={ALL_OPTION}>
              {t('filterMachine.allFacilities')}
            </Option>
            <Option value="">{t('filterMachine.unset')}</Option>
            {facilityOptions.map((facility) => {
              return (
                <Option key={facility} value={facility}>
                  {facility}
                </Option>
              );
            })}
          </BaseSelect>
        </Col>
        <Col>
          <h5 className="selection-label">{t('filterMachine.object')}</h5>
          <BaseSelect
            className="custom-select min-w-180"
            defaultValue={selectedObject}
            onChange={handleSelectedObject}>
            <Option value={ALL_OPTION}>{t('filterMachine.allObjects')}</Option>
            <Option value="">{t('filterMachine.unset')}</Option>
            {objectOptions.map((object) => {
              return (
                <Option key={object} value={object}>
                  {object}
                </Option>
              );
            })}
          </BaseSelect>
        </Col>
        <Col>
          <h5 className="selection-label">{t('filterMachine.machine')}</h5>
          <BaseSelect
            className="custom-select min-w-180"
            defaultValue={selectedMachineId}
            onChange={handleSelectedMachine}>
            <Option value={ALL_OPTION}>{t('filterMachine.allMachines')}</Option>
            {machineOptions.map((machine) => {
              return (
                <Option key={machine.id} value={machine.id}>
                  {getMachineType(machine.materialNumber)} / {machine.alias}
                </Option>
              );
            })}
          </BaseSelect>
        </Col>
      </Row>
    );
  }, [
    t,
    selectedFacility,
    handleSelectedFacility,
    facilityOptions,
    selectedObject,
    handleSelectedObject,
    objectOptions,
    selectedMachineId,
    handleSelectedMachine,
    machineOptions
  ]);

  return [
    MachineFilter,
    {
      facility: selectedFacility,
      object: selectedObject,
      machineId: selectedMachineId,
      machineIds: selectedMachineIds
    }
  ];
};
